<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSession } from '@/stores/session'
import FiltreSociete from '@/components/FiltreSociete.vue'

// Mise en page calquée sur l'espace client BUXIDA
// (`ubicx/buxapi` → `BUXIDA_ESPACECLIENT/src/layouts/MainLayout.vue`), qui
// est le modèle de référence : grille `260px 1fr`, navigation verticale à
// GAUCHE, et sur mobile une barre supérieure à bouton-sandwich qui ouvre la
// même barre latérale en tiroir.
//
// La première version de cet écran portait la navigation en haut. Ce n'était
// pas une transposition ratée : le plan de travail avait retenu la PILE de
// BUXIDA (« 14 vues, 2 layouts, 1 store, aucun framework UI ») mais rien de
// son interface, et le dépôt n'était pas rattaché à la session. Corrigé le
// 04/11/2026, dépôt en main.
//
// CE QUI NE SE TRANSPOSE PAS. BUXIDA affiche le logo DU CENTRE dans l'entête
// de la barre : son espace client sert un centre à la fois. Ici il n'y en a
// pas un — l'espace réunit BOXEA, IZIBOX et DOMICIALIZ dans la même liste.
// L'entête porte donc la marque ombrelle, et c'est le sélecteur de société
// qui joue le rôle que tient le logo chez BUXIDA.

const session = useSession()
const router = useRouter()
const route = useRoute()
const erreur = ref('')

// Tiroir mobile. Refermé à chaque changement d'adresse : sans cela, naviguer
// laisserait le tiroir ouvert par-dessus la page qu'on vient de demander.
const tiroirOuvert = ref(false)
watch(() => route.fullPath, () => { tiroirOuvert.value = false })

// Le profil est chargé une fois pour toutes : il porte le nom affiché, la
// liste des sociétés dont dépend le filtre, et le drapeau d'aperçu.
onMounted(async () => {
  if (session.chargee) return
  try {
    await session.charger()
  } catch (e) {
    // 401 déjà tenté et rejoué par le client HTTP : la session est finie.
    if (e.response && e.response.status === 401) {
      session.deconnecter()
      router.push({ name: 'demande-lien' })
      return
    }
    erreur.value = 'Vos informations n’ont pas pu être chargées.'
  }
})

function deconnecter() {
  session.deconnecter()
  router.push({ name: 'demande-lien' })
}
</script>

<template>
  <div class="coque">
    <!-- APERÇU ADMINISTRATEUR — pleine largeur, au-dessus de tout, et non
         dans un coin. Ce qui est affiché sont les données d'une personne
         réelle, et un bandeau discret finit par ne plus être lu. Le drapeau
         vient de l'API (/client/moi), donc un rechargement ne le fait pas
         oublier. Traitement visuel repris de la bannière « Mode
         gestionnaire » de BUXIDA. -->
    <div v-if="session.apercu" class="bandeau-apercu">
      <div class="ba-contenu">
        <span class="ba-etiquette">Aperçu administrateur</span>
        <span class="ba-texte">
          Vous consultez l’espace de
          <b>{{ session.nomAffiche || 'ce client' }}</b> — lecture seule,
          et cet aperçu expire au bout de quelques minutes.
        </span>
      </div>
      <button type="button" class="bouton discret ba-fermer" @click="deconnecter">
        Fermer l’aperçu
      </button>
    </div>

    <!-- Barre supérieure : mobile uniquement (< 768px). -->
    <header class="barre-mobile">
      <button
        class="bouton discret sandwich"
        type="button"
        :aria-label="tiroirOuvert ? 'Fermer le menu' : 'Ouvrir le menu'"
        :aria-expanded="tiroirOuvert"
        @click="tiroirOuvert = !tiroirOuvert"
      >
        <span class="sandwich-icone"><span></span><span></span><span></span></span>
      </button>
      <strong>Espace client</strong>
    </header>

    <!-- Voile cliquable pour refermer le tiroir. -->
    <div v-if="tiroirOuvert" class="voile" @click="tiroirOuvert = false"></div>

    <aside class="laterale" :class="{ ouvert: tiroirOuvert }">
      <div class="marque">
        <strong>REUNIBOX</strong>
        <span class="discret">Espace client</span>
      </div>

      <nav>
        <RouterLink to="/tableau-de-bord" active-class="actif">Accueil</RouterLink>
        <RouterLink to="/contrats" active-class="actif">Mes contrats</RouterLink>
        <RouterLink to="/factures" active-class="actif">Mes factures</RouterLink>
        <RouterLink to="/documents" active-class="actif">Mes documents</RouterLink>
        <RouterLink to="/demandes" active-class="actif">Mes demandes</RouterLink>
        <RouterLink to="/profil" active-class="actif">Mon profil</RouterLink>
      </nav>

      <div class="laterale-pied">
        <!-- Le filtre de marque prend la place que le logo du centre occupe
             chez BUXIDA : c'est lui qui dit « de quelle enseigne je parle ». -->
        <FiltreSociete v-if="session.filtrable" />
        <div v-if="session.nomAffiche" class="utilisateur">
          <div class="nom">{{ session.nomAffiche }}</div>
          <div v-if="session.client && session.client.email" class="courriel discret">
            {{ session.client.email }}
          </div>
        </div>
        <button class="bouton discret" type="button" @click="deconnecter">
          {{ session.apercu ? 'Fermer l’aperçu' : 'Se déconnecter' }}
        </button>
      </div>
    </aside>

    <main class="contenu">
      <p v-if="erreur" class="message erreur">{{ erreur }}</p>
      <slot />
    </main>
  </div>
</template>
